import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { FaLocationDot, FaMagnifyingGlass, FaCircleCheck, FaPeopleGroup } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import "./BePartOfTheMap.css";
import StorySubmitForm from "./StorySubmitForm";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease, delay } },
});
const fadeIn = (delay = 0) => ({
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.65, ease, delay } },
});

/* ── Four process steps ──────────────────────────────────────── */
const STEPS = [
  {
    num: "1.",
    label: "Submit",
    Icon: FaEdit,
    desc: "Share your location and your story.",
  },
  {
    num: "2.",
    label: "Review",
    Icon: FaMagnifyingGlass,
    desc: "Our team reviews each submission.",
  },
  {
    num: "3.",
    label: "Approved",
    Icon: FaCircleCheck,
    desc: "Your story goes live on the impact map.",
  },
  {
    num: "4.",
    label: "Inspire",
    Icon: FaPeopleGroup,
    desc: "Help others find hope and strength.",
  },
];

/* ── Arrow connector between steps ─────────────────────────── */
function StepArrow() {
  return (
    <div className="bpm__arrow" aria-hidden="true">
      <svg viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="8" x2="30" y2="8" stroke="currentColor" strokeWidth="1.2" />
        <path d="M26 3 L34 8 L26 13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

export default function BePartOfTheMap() {
  const [showForm, setShowForm] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section className="bpm" ref={ref}>
      {/* ══ TOP ZONE — cream ══════════════════════════════════ */}
      <div className="bpm__top">
        <div className="bpm__top-inner container-fg container-2xxl">
          {/* Left: map pin icon + heading + sub */}
          <motion.div className="bpm__left" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="bpm__pin-circle" aria-hidden="true">
              <FaLocationDot />
              {/* Plus badge */}
              <span className="bpm__pin-plus" aria-hidden="true">
                +
              </span>
            </div>
            <div className="bpm__left-text">
              <h2 className="bpm__heading u-display">Be Part of the Map</h2>
              <p className="bpm__sub">
                Share your story and help others see
                <br />
                they're not alone on this path.
              </p>
            </div>
          </motion.div>

          {/* Centre: CTA button */}
          <motion.div className="bpm__centre" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <a href="#submit-story" className="bpm__cta" onClick={() => setShowForm(true)}>
              Submit Your Story
            </a>
          </motion.div>

          {/* Right: botanical + disclaimer */}
          <motion.div className="bpm__right " variants={fadeIn(0.16)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="bpm__botanical" aria-hidden="true">
              {/* Inline botanical SVG — leaf sprig */}
              <img src="assets/flower.png" alt="" />
            </div>
            <p className="bpm__disclaimer">
              All stories are reviewed
              <br />
              by our team before
              <br />
              being published.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ══ BOTTOM ZONE — dark forest ════════════════════════ */}
      <div className="bpm__bottom">
        <div className="bpm__bottom-inner container-fg container-2xxl">
          {/* Eyebrow label */}
          <motion.p className="bpm__eyebrow u-eyebrow" variants={fadeIn(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            How It Works
          </motion.p>

          {/* Steps row */}
          <div className="bpm__steps">
            {STEPS.map((step, i) => (
              <div key={step.label} className="bpm__step-group">
                <motion.div className="bpm__step" variants={fadeUp(0.08 + i * 0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
                  {/* Icon circle */}
                  <div className="bpm__step-icon" aria-hidden="true">
                    <step.Icon />
                  </div>

                  {/* Number + label */}
                  <div className="bpm__step-label-row">
                    <span className="bpm__step-num">{step.num}</span>
                    <span className="bpm__step-label">{step.label.toUpperCase()}</span>
                  </div>

                  {/* Description */}
                  <p className="bpm__step-desc">{step.desc}</p>
                </motion.div>

                {/* Arrow between steps — not after last */}
                {i < STEPS.length - 1 && (
                  <motion.div variants={fadeIn(0.12 + i * 0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
                    <StepArrow />
                  </motion.div>
                )}
              </div>
            ))}
          </div>

          {/* Share Your Story CTA */}
          <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <button type="button" className="bpm__submit-cta" onClick={() => setShowForm(true)}>
              Share Your Story on the Map →
            </button>
          </div>
        </div>
      </div>
      {showForm && <StorySubmitForm onClose={() => setShowForm(false)} />}
    </section>
  );
}
